/**
 * P-21 (D-152, D-153) — the pure rules of a customer user's portal checkboxes.
 * Mirrors the server (`services/portal.service.ts` defaults,
 * `services/customerAccess.service.ts` ceiling). No React, no i18n instance:
 * tested in src/utils/__tests__/portalPermsModel.test.ts.
 *
 *   הקריאות שלו         — always (not a checkbox: shown ticked and locked)
 *   כל הקריאות של החברה → view_all_tickets (D-158 QX-86: a real checkbox; the
 *                         manager rank's default; "מאשר קריאות" always sees all)
 *   פרויקטים והתקדמות   → view_projects
 *   מסמכים              → view_documents
 *   דוחות שעות          → view_hours
 *   חיובים וחוב         → view_billing
 *   מאשר קריאות         → approve_tickets (the AccessDraft's `approveTickets`)
 */

export const CAPPED_PERM_KEYS = ['view_projects', 'view_documents', 'view_hours', 'view_billing'] as const;
export type CappedPermKey = typeof CAPPED_PERM_KEYS[number];

/** The four capped checkboxes + "כל הקריאות" (not capped). "מאשר קריאות" lives on the access draft itself. */
export type PermDraft = Record<CappedPermKey, boolean> & { view_all_tickets: boolean };
/** What the OTE staff member may tick (GET /api/portal-access/ceiling). */
export type PermCeiling = Record<CappedPermKey, boolean>;

export const NO_CAP: PermCeiling = { view_projects: true, view_documents: true, view_hours: true, view_billing: true };

export const PERM_LABEL_KEY: Record<CappedPermKey, string> = {
  view_projects: 'portalAccess.perm.view_projects',
  view_documents: 'portalAccess.perm.view_documents',
  view_hours: 'portalAccess.perm.view_hours',
  view_billing: 'portalAccess.perm.view_billing',
};

/** "מנהל מטעם הלקוח" and "איש קשר ראשי" share the manager rank (D-100). */
export function isManagerLevel(level: string | null | undefined): boolean {
  return level === 'customer_manager' || level === 'primary_contact';
}

export function isManagerRank(portalRole: string | null | undefined): boolean {
  return portalRole === 'customer_portal_manager';
}

/**
 * The checkboxes a level starts with (D-152): the manager — everything (bar
 * "מאשר קריאות", which is the draft's own flag); a customer employee — only
 * their own tickets (D-100). Never above the caller's ceiling.
 */
export function levelDefaultPerms(level: string | null | undefined, ceiling: PermCeiling = NO_CAP): PermDraft {
  const on = isManagerLevel(level);
  return {
    view_all_tickets: on,
    view_projects: on && ceiling.view_projects,
    view_documents: on && ceiling.view_documents,
    view_hours: on && ceiling.view_hours,
    view_billing: on && ceiling.view_billing,
  };
}

/** A stored grant's ACTUAL values: the rank's defaults overlaid by the bag (server rule). */
export function effectivePerms(portalRole: string | null | undefined, bag: Record<string, unknown> | null | undefined): PermDraft & { approve_tickets: boolean } {
  const on = isManagerRank(portalRole);
  const b = bag && typeof bag === 'object' ? bag : {};
  const pick = (k: string, dflt: boolean) => (typeof b[k] === 'boolean' ? (b[k] as boolean) : dflt);
  return {
    view_all_tickets: pick('view_all_tickets', on),
    view_projects: pick('view_projects', on),
    view_documents: pick('view_documents', on),
    view_hours: pick('view_hours', on),
    view_billing: pick('view_billing', on),
    approve_tickets: pick('approve_tickets', false),
  };
}

/** "כל הקריאות של החברה" — the key (D-158 QX-86), or the ticket approver (always). */
export function seesAllTickets(viewAllTickets: boolean, approveTickets: boolean): boolean {
  return viewAllTickets || approveTickets;
}

/** The body's `permissions`: the four checkboxes + the approver flag, capped. */
export function permsPayload(perms: PermDraft, approveTickets: boolean, ceiling: PermCeiling = NO_CAP): Record<string, boolean> {
  const out: Record<string, boolean> = { approve_tickets: approveTickets, view_all_tickets: perms.view_all_tickets };
  for (const k of CAPPED_PERM_KEYS) out[k] = perms[k] && ceiling[k];
  return out;
}

/**
 * D-153 (b): "רואה: קריאות, פרויקטים" — i18n keys of what this person sees,
 * in the checkbox order. Tickets first: all of the company's, or their own.
 */
export function seesSummaryKeys(portalRole: string | null | undefined, bag: Record<string, unknown> | null | undefined): string[] {
  const eff = effectivePerms(portalRole, bag);
  const keys = [seesAllTickets(eff.view_all_tickets, eff.approve_tickets) ? 'portalAccess.sees.allTickets' : 'portalAccess.sees.ownTickets'];
  if (eff.view_projects) keys.push('portalAccess.sees.projects');
  if (eff.view_documents) keys.push('portalAccess.sees.documents');
  if (eff.view_hours) keys.push('portalAccess.sees.hours');
  if (eff.view_billing) keys.push('portalAccess.sees.billing');
  return keys;
}

/** Staff-role logins (`users.role`) — in the customer tab they have no employee record. */
export function isStaffLogin(role: string | null | undefined): boolean {
  return role === 'admin' || role === 'agent';
}

/**
 * D-152 (Q-115): a customer-tab row that needs fixing, or null. Every row of
 * the customer tab already has no employee record (that is how the tabs split).
 */
export function stuckKind(g: { contact_id?: string | null; user_role?: string | null; no_contact?: boolean | null }): 'no_employee_record' | 'unclassified' | null {
  if (isStaffLogin(g.user_role)) return 'no_employee_record';
  // W-07: a customer employee created without a contact on purpose is not stuck.
  if (!g.contact_id && !g.no_contact) return 'unclassified';
  return null;
}
